
// src/components/PublishingEssentialsEditor.tsx
import React, { useState } from 'react';
import { PublishingEssentials } from '../types';
import { labelClass, textareaClass, actionButtonClass } from '../constants';
import { useAppContext } from '../contexts/AppContext';
import { 
    useViewEditMode, 
    ViewEditSection, 
    SaveCancelButtons, 
    DisplayField, 
    DisplayGrid 
} from '../utils/viewEditUtils';
import ProfessionalElementsEditor from './ProfessionalElementsEditor';
import { getDefaultProfessionalElements } from '../utils/publishingElements';
import { useOptimizedSave } from '../hooks/useOptimizedSave';

interface PublishingEssentialsEditorProps {
    publishingEssentials: PublishingEssentials;
    onPublishingEssentialsChange: <K extends keyof PublishingEssentials>(field: K, value: PublishingEssentials[K]) => void;
}

const PublishingEssentialsEditor: React.FC<PublishingEssentialsEditorProps> = ({ publishingEssentials, onPublishingEssentialsChange }) => {
    const appContext = useAppContext();
    const { activeEditSection, handleEditToggle, handleSaveAndExit, handleCancelEdit } = useViewEditMode();
    const [showProfessionalElements, setShowProfessionalElements] = useState(false);
    const { savePublishingEssentials } = useOptimizedSave();

    const handleSaveChanges = async () => {
        try {
            // Get blueprint state management functions from optimized data context
            const { blueprint } = appContext.optimizedData || {};
            
            await savePublishingEssentials.mutateAsync({
                projectId: appContext.dbProject?.uuid || appContext.dbProject?.id?.toString() || '',
                publishingEssentials,
                setBlueprintSaving: blueprint?.setBlueprintSaving
            });
            handleSaveAndExit();
        } catch (error) {
            console.error('Failed to save changes:', error);
        }
    };
    
    const fields: Array<{ key: keyof PublishingEssentials; label: string; placeholder: string; rows?: number }> = [
        { key: 'metadataFileNotes', label: 'Metadata File Notes (ONIX/XML or JSON)', placeholder: 'Notes on keywords, synopsis, age group, BISAC codes, etc. for your metadata file. Markdown supported.', rows: 5 },
        { key: 'queryLetterDraft', label: 'Query Letter Draft (Traditional Publishing)', placeholder: 'Draft your query letter. Include book overview, market positioning, author platform. Markdown supported.', rows: 10 },
        { key: 'marketingBlurb', label: 'Marketing Blurb / Elevator Pitch', placeholder: 'Craft a 1-2 line hook or a short blurb for listings, ads, or back cover. Markdown supported.', rows: 4 },
        { key: 'coverDesignBrief', label: 'Cover Design Brief / Notes', placeholder: 'Notes for your cover designer: concepts, imagery, style, comparable titles. Markdown supported.', rows: 6 },
        { key: 'isbnNotes', label: 'ISBN Notes', placeholder: 'Keep track of ISBNs for different editions (e.g., Print ISBN: XXX-X-XXXXX-XX-X). Markdown supported.', rows: 3 },
        { key: 'copyrightRegistrationNotes', label: 'Copyright Registration Notes', placeholder: 'Notes on copyright filing (e.g., Filed on YYYY-MM-DD, Registration Number: XXXXX). Markdown supported.', rows: 3 },
    ];

    // Group fields for better organization
    const marketingFields = fields.slice(0, 3); // Metadata, Query Letter, Marketing Blurb
    const designFields = fields.slice(3, 4); // Cover Design Brief
    const legalFields = fields.slice(4); // ISBN, Copyright

    const handleProfessionalElementsChange = (elements: any) => {
        onPublishingEssentialsChange('professionalElements', elements);
    };

    const renderEditForm = (fieldsToRender: typeof fields) => (
        <div className="space-y-4">
            {fieldsToRender.map(({ key, label, placeholder, rows = 3 }) => (
                <div key={String(key)}>
                    <label htmlFor={`pe-${String(key)}`} className={`${labelClass} mb-1.5`}>{label}:</label>
                    <textarea
                        id={`pe-${String(key)}`}
                        value={String(publishingEssentials[key as keyof PublishingEssentials] || '')}
                        onChange={(e) => onPublishingEssentialsChange(key as (string & keyof PublishingEssentials), e.target.value as any)}
                        placeholder={placeholder}
                        className={`${textareaClass} min-h-[${rows * 1.5}rem]`}
                        rows={rows}
                    />
                     <p className="text-xs text-gray-500 mt-1">Supports Markdown for formatting.</p>
                </div>
            ))}
        </div>
    );

    const renderDisplayView = (fieldsToRender: typeof fields) => (
        <div className="space-y-4">
            {fieldsToRender.map(({ key, label }) => (
                <DisplayField
                    key={String(key)}
                    label={label}
                    value={publishingEssentials[key as keyof PublishingEssentials] as string | number | undefined}
                    background={true}
                />
            ))}
        </div>
    );

    return (
        <div className="space-y-6 p-1 h-full overflow-y-auto">
            <h2 className="text-2xl font-semibold text-gray-800 border-b pb-3 mb-6">Publishing Essentials</h2>
            <p className="text-sm text-gray-600 mb-6">Manage notes and drafts related to the publishing process. All text fields support Markdown for formatting.</p>
            
            {/* Marketing & Submission Section */}
            <ViewEditSection
                title="Marketing & Submission"
                subtitle="Metadata, query letters, and marketing materials"
                isActive={activeEditSection === 'marketing'}
                onEditToggle={() => handleEditToggle('marketing')}
            >
                {activeEditSection === 'marketing' ? (
                    <>
                        {renderEditForm(marketingFields)}
                        <SaveCancelButtons
                            onSave={handleSaveChanges}
                            onCancel={handleCancelEdit}
                        />
                    </>
                ) : (
                    renderDisplayView(marketingFields)
                )}
            </ViewEditSection>

            {/* Design & Creative Section */}
            <ViewEditSection
                title="Design & Creative"
                subtitle="Cover design and visual elements"
                isActive={activeEditSection === 'design'}
                onEditToggle={() => handleEditToggle('design')}
            >
                {activeEditSection === 'design' ? (
                    <>
                        {renderEditForm(designFields)}
                        <SaveCancelButtons
                            onSave={handleSaveChanges}
                            onCancel={handleCancelEdit}
                        />
                    </>
                ) : (
                    renderDisplayView(designFields)
                )}
            </ViewEditSection>

            {/* Legal & Registration Section */}
            <ViewEditSection
                title="Legal & Registration"
                subtitle="ISBN, copyright, and legal documentation"
                isActive={activeEditSection === 'legal'}
                onEditToggle={() => handleEditToggle('legal')}
            >
                {activeEditSection === 'legal' ? (
                    <>
                        {renderEditForm(legalFields)}
                        <SaveCancelButtons
                            onSave={handleSaveChanges}
                            onCancel={handleCancelEdit}
                        />
                    </>
                ) : (
                    renderDisplayView(legalFields)
                )}
            </ViewEditSection>

            {/* Professional Publishing Elements Section */}
            <ViewEditSection
                title="Professional Publishing Elements"
                subtitle="Industry-standard metadata, legal notices, and production credits"
                isActive={false}
                onEditToggle={() => {}}
            >
                <div className="space-y-4">
                    <DisplayField
                        label="Publisher Information"
                        value={publishingEssentials.professionalElements?.publisherInfo?.publisherName || 'Not configured'}
                        background={true}
                    />
                    <DisplayField
                        label="Copyright Notice"
                        value={publishingEssentials.professionalElements?.legalNotices?.copyrightNotice || 'Not configured'}
                        background={true}
                    />
                    <DisplayField
                        label="ISBN-13"
                        value={publishingEssentials.professionalElements?.libraryOfCongressData?.isbn13 || 'Not assigned'}
                        background={true}
                    />
                    <DisplayField
                        label="Production Credits"
                        value={
                            publishingEssentials.professionalElements?.productionCredits?.editor 
                                ? `Editor: ${publishingEssentials.professionalElements.productionCredits.editor}` 
                                : 'No credits configured'
                        }
                        background={true}
                    />
                    
                    <div className="flex space-x-3 pt-4">
                        <button
                            onClick={() => setShowProfessionalElements(true)}
                            className={`${actionButtonClass} text-sm`}
                        >
                            Configure Professional Elements
                        </button>
                        
                        {!publishingEssentials.professionalElements && (
                            <button
                                onClick={() => {
                                    const defaultElements = getDefaultProfessionalElements();
                                    handleProfessionalElementsChange(defaultElements);
                                }}
                                className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm"
                            >
                                Initialize with Defaults
                            </button>
                        )}
                    </div>
                </div>
            </ViewEditSection>

            <p className="text-xs text-gray-500 mt-6">
                These are typically not part of the book's content but are necessary for submission, distribution, or marketing. Remember to "Save Project" to persist your changes.
            </p>

            {/* Professional Elements Editor Modal */}
            {publishingEssentials.professionalElements && (
                <ProfessionalElementsEditor
                    elements={publishingEssentials.professionalElements}
                    onChange={handleProfessionalElementsChange}
                    bookTitle="Book Title" // TODO: Get from project state
                    authorName="Author Name" // TODO: Get from project state
                    isOpen={showProfessionalElements}
                    onClose={() => setShowProfessionalElements(false)}
                />
            )}
        </div>
    );
};

export default PublishingEssentialsEditor;
